import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  CheckCircle,
  Trophy,
  Clock,
  Target,
  Star,
  ArrowRight,
  RotateCcw,
  Home
} from 'lucide-react'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import { StudySessionResult } from '../types/flashcard'

interface SessionStats {
  cardsStudied: number
  cardsCorrect: number
  cardsIncorrect: number
  duration: number
  score: number
  xpEarned: number
  badgesEarned: string[]
}

const SessionComplete: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  
  const [sessionStats, setSessionStats] = useState<SessionStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadSessionData = async () => {
      try {
        // location.state'ten sessionResult'ı al
        const sessionResult = location.state?.sessionResult as StudySessionResult
        
        if (sessionResult) {
          // location.state'ten gelen veriyi kullan
          const stats: SessionStats = {
            cardsStudied: sessionResult.cardsStudied,
            cardsCorrect: sessionResult.cardsCorrect,
            cardsIncorrect: sessionResult.cardsIncorrect,
            duration: sessionResult.duration,
            score: sessionResult.score,
            xpEarned: sessionResult.xpEarned,
            badgesEarned: sessionResult.badgesEarned || []
          }
          setSessionStats(stats)
        } else if (sessionId && sessionId !== 'local' && sessionId !== 'review') {
          // Gerçek sessionId varsa Firebase'den getir (şimdilik hata ver)
          setError('Firebase oturum verileri henüz desteklenmiyor.')
        } else {
          setError('Oturum verileri bulunamadı. Lütfen tekrar çalışma yapın.')
        }
      } catch (err) {
        console.error('Oturum verileri yüklenirken hata:', err)
        setError('Oturum verileri yüklenirken bir hata oluştu.')
      } finally {
        setLoading(false)
      }
    }

    loadSessionData()
  }, [sessionId, location.state])

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const getScoreColor = (score: number): string => {
    if (score >= 90) return 'text-green-600'
    if (score >= 70) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getScoreMessage = (score: number): string => {
    if (score >= 90) return 'Mükemmel! 🎉'
    if (score >= 80) return 'Çok İyi! 👏'
    if (score >= 70) return 'İyi! 👍'
    if (score >= 60) return 'Fena Değil 🙂'
    return 'Daha Çok Çalışmalısın 💪'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !sessionStats) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {error || 'Oturum sonuçları yüklenemedi'}
          </h2>
          <Button onClick={() => navigate('/flashcards')}>
            Flashcard'lara Dön
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Çalışma Tamamlandı!
          </h1>
          <p className="text-gray-600">
            {getScoreMessage(sessionStats.score)}
          </p>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Target className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {sessionStats.cardsStudied}
            </div>
            <div className="text-sm text-gray-600">Çalışılan Kart</div>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-green-600 mb-1">
              {sessionStats.cardsCorrect}
            </div>
            <div className="text-sm text-gray-600">Doğru Cevap</div>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {formatDuration(sessionStats.duration)}
            </div>
            <div className="text-sm text-gray-600">Süre</div>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Star className="w-6 h-6 text-purple-600" />
            </div>
            <div className={`text-2xl font-bold mb-1 ${getScoreColor(sessionStats.score)}`}>
              {sessionStats.score}%
            </div>
            <div className="text-sm text-gray-600">Başarı Oranı</div>
          </Card>
        </div>

        {/* Detailed Results */}
        <Card className="p-8 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Detaylı Sonuçlar</h2>
          
          <div className="space-y-4">
            {/* Score Bar */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Başarı Oranı</span>
                <span className={`text-sm font-semibold ${getScoreColor(sessionStats.score)}`}>
                  {sessionStats.score}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${
                    sessionStats.score >= 80 ? 'bg-green-500' : 
                    sessionStats.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${sessionStats.score}%` }}
                ></div>
              </div>
            </div>

            {/* XP Earned */}
            {sessionStats.xpEarned > 0 && (
              <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">XP Kazandın!</div>
                    <div className="text-sm text-gray-600">Harika performans</div>
                  </div>
                </div>
                <div className="text-2xl font-bold text-yellow-600">
                  +{sessionStats.xpEarned}
                </div>
              </div>
            )}

            {/* Badges Earned */}
            {sessionStats.badgesEarned.length > 0 && (
              <div className="p-4 bg-purple-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Kazanılan Rozetler</h3>
                <div className="flex flex-wrap gap-2">
                  {sessionStats.badgesEarned.map((badge, index) => (
                    <Badge key={index} className="bg-purple-100 text-purple-800">
                      {badge}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/flashcards')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
          >
            <Home className="w-5 h-5 mr-2" />
            Ana Sayfaya Dön
          </Button>
          
          <Button
            variant="outline"
            onClick={() => window.history.back()}
            className="px-8 py-3"
          >
            <RotateCcw className="w-5 h-5 mr-2" />
            Tekrar Çalış
          </Button>
          
          <Button
            variant="outline"
            onClick={() => navigate('/flashcards/stats')}
            className="px-8 py-3"
          >
            <Target className="w-5 h-5 mr-2" />
            İstatistikleri Gör
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SessionComplete